/**
 * COLLECTION PARSER UTILITY
 * 
 * Shared parsing logic for collection/release fields.
 * Used by both:
 *   - Import process (clean data as it comes in)
 *   - Data Cleanup Wizard (fix existing data)
 * 
 * Extracts:
 *   - Collection name (cleaned) - for collection releases
 *   - Event source (PPU, HHC, etc.) - for event releases
 *   - Event theme - for events that have themes instead of collections (PPU, HHC, etc.)
 *   - Release year (2015-2030)
 *   - Release season (from season words OR month names OR numeric months OR holidays)
 *   - Exclusive retailer - from "Exclusive to [store]" patterns
 *   - Box name - from known box names or "Box:" patterns
 *   - Group name - from "FB Group:", "[Name] Custom" patterns
 *   - Convention name - from "Polish Con", "Convention:" patterns
 *   - Collab with & theme - from "Collab with [Name]: [Theme]" patterns
 * 
 * KEY DISTINCTION:
 *   - PPU, HHC, etc. are "theme-based events" - extra text goes to eventTheme
 *   - Other events/collections - extra text goes to collectionName
 * 
 * SMART SEASON DETECTION:
 *   - Only extracts season from season/month/holiday words when followed by a year
 *   - "Summer 2024" → releaseSeason: Summer, releaseYear: 2024
 *   - "Summer Breeze" → collectionName: "Summer Breeze" (no season extraction)
 * 
 * EXCLUSIVE/BOX/GROUP/COLLAB DETECTION:
 *   - "Exclusive to Color4Nails" → exclusiveTo: "Color4Nails", releaseType: "exclusive"
 *   - "For the Love of Polish Box" → boxName: "For the Love of Polish Box", releaseType: "box"
 *   - "Holo Hangout Custom" → groupName: "Holo Hangout", releaseType: "groupCustom"
 *   - "Polish Con 2024" → conventionName: "Polish Con", releaseType: "convention"
 *   - "Collab with Cupcake Polish: 4th Anniversary" → collabWith: "Cupcake Polish", collabTheme: "4th Anniversary"
 */

// ============================================
// EVENT KEYWORDS
// ============================================

// Event keywords to detect with their canonical/full names
// Multiple spellings map to the same canonical name
export const EVENT_KEYWORD_MAP: Record<string, string> = {
  // Polish & Beauty Expo variants
  'PBE': 'Polish & Beauty Expo',
  'Polish Beaut Expo': 'Polish & Beauty Expo',
  'Polish Beauty Expo': 'Polish & Beauty Expo',
  'Polish & Beauty Expo': 'Polish & Beauty Expo',
  // Polish Pick Up variants
  'PPU': 'Polish Pick Up',
  'Polish Pick Up': 'Polish Pick Up',
  'Polish Pickup': 'Polish Pick Up',
  'PolishPickUp': 'Polish Pick Up',
  // Hella Handmade variants
  'HHC': 'Hella Handmade Creations',
  'Hella Handmade': 'Hella Handmade Creations',
  'Hella Handmade Creations': 'Hella Handmade Creations',
  // Indie Pick Up variants
  'IP': 'Indie Pick Up',
  'Indie Pickup': 'Indie Pick Up',
  'Indie Pick Up': 'Indie Pick Up',
  // Box subscriptions (theme-based)
  'Polished Gamers': 'Polished Gamers Box',
  'Polished Gamers Box': 'Polished Gamers Box',
  // Generic event types
  'Expo': 'Expo',
  'Convention': 'Convention',
  'Mystery Box': 'Mystery Box',
  'Subscription': 'Subscription Box',
  'Advent': 'Advent Calendar',
  'Calendar': 'Calendar',
};

// ============================================
// THEME-BASED EVENTS
// ============================================

/**
 * These events have monthly/periodic THEMES, not collections.
 * When we find extra text after parsing these events, it goes to `eventTheme` not `collectionName`.
 * 
 * Example: "PPU Rainbows March 2023"
 *   → eventSource: "Polish Pick Up"
 *   → eventTheme: "Rainbows"  (NOT collectionName!)
 *   → releaseSeason: "Spring"
 *   → releaseYear: 2023
 */
export const THEME_BASED_EVENTS = [
  'Polish Pick Up',
  'Hella Handmade Creations',
  'Indie Pick Up',
  'Polish & Beauty Expo',
  'Polished Gamers Box',
  'Monthly Box',
  'Subscription Box',
  'Mystery Box',
  'Advent Calendar',
] as const;

/**
 * Check if an event uses themes instead of collections
 */
export function isThemeBasedEvent(eventName: string): boolean {
  return THEME_BASED_EVENTS.some(
    e => e.toLowerCase() === eventName.toLowerCase()
  );
}

// ============================================
// SEASONS & MONTHS
// ============================================

// Season words to detect
export const SEASONS = ['Spring', 'Summer', 'Fall', 'Autumn', 'Winter'] as const;

// Text months mapped to their seasons
// Handles: "Voltron Aug 2021" → Season: Summer
export const MONTHS_TO_SEASON: Record<string, string> = {
  // Winter (Dec, Jan, Feb)
  'jan': 'Winter', 'january': 'Winter',
  'feb': 'Winter', 'february': 'Winter',
  'dec': 'Winter', 'december': 'Winter',
  // Spring (Mar, Apr, May)
  'mar': 'Spring', 'march': 'Spring',
  'apr': 'Spring', 'april': 'Spring',
  'may': 'Spring',
  // Summer (Jun, Jul, Aug)
  'jun': 'Summer', 'june': 'Summer',
  'jul': 'Summer', 'july': 'Summer',
  'aug': 'Summer', 'august': 'Summer',
  // Fall (Sep, Oct, Nov)
  'sep': 'Fall', 'sept': 'Fall', 'september': 'Fall',
  'oct': 'Fall', 'october': 'Fall',
  'nov': 'Fall', 'november': 'Fall',
};

// Numeric months mapped to their seasons
// Handles: "2021/10" → Season: Fall, "2016/01" → Season: Winter
export const NUMERIC_MONTHS_TO_SEASON: Record<string, string> = {
  '01': 'Winter', '1': 'Winter',
  '02': 'Winter', '2': 'Winter',
  '12': 'Winter',
  '03': 'Spring', '3': 'Spring',
  '04': 'Spring', '4': 'Spring',
  '05': 'Spring', '5': 'Spring',
  '06': 'Summer', '6': 'Summer',
  '07': 'Summer', '7': 'Summer',
  '08': 'Summer', '8': 'Summer',
  '09': 'Fall', '9': 'Fall',
  '10': 'Fall',
  '11': 'Fall',
};

// Text months mapped to month number (1-12)
// Handles: "Voltron Aug 2021" → Month: 8
export const MONTHS_TO_NUMBER: Record<string, number> = {
  'jan': 1, 'january': 1,
  'feb': 2, 'february': 2,
  'mar': 3, 'march': 3,
  'apr': 4, 'april': 4,
  'may': 5,
  'jun': 6, 'june': 6,
  'jul': 7, 'july': 7,
  'aug': 8, 'august': 8,
  'sep': 9, 'sept': 9, 'september': 9,
  'oct': 10, 'october': 10,
  'nov': 11, 'november': 11,
  'dec': 12, 'december': 12,
};

// Numeric month strings to numbers
// Handles: "2021/10" → Month: 10
export const NUMERIC_MONTHS_TO_NUMBER: Record<string, number> = {
  '01': 1, '1': 1,
  '02': 2, '2': 2,
  '03': 3, '3': 3,
  '04': 4, '4': 4,
  '05': 5, '5': 5,
  '06': 6, '6': 6,
  '07': 7, '7': 7,
  '08': 8, '8': 8,
  '09': 9, '9': 9,
  '10': 10,
  '11': 11,
  '12': 12,
};

// ============================================
// HOLIDAYS → SEASONS
// ============================================

/**
 * Holiday words mapped to their seasons.
 * Only extracted when followed by a year (e.g., "Christmas 2024" → Winter)
 * 
 * Examples:
 *   "Christmas 2024" → releaseSeason: Winter, releaseYear: 2024
 *   "Halloween 2023" → releaseSeason: Fall, releaseYear: 2023
 *   "Black Friday 2024" → releaseSeason: Fall, releaseYear: 2024
 */
export const HOLIDAYS_TO_SEASON: Record<string, string> = {
  // Winter holidays
  'christmas': 'Winter',
  'xmas': 'Winter',
  'holiday': 'Winter',
  'holidays': 'Winter',
  'valentine': 'Winter',
  'valentines': 'Winter',
  "valentine's": 'Winter',
  'new year': 'Winter',
  'new years': 'Winter',
  "new year's": 'Winter',
  'newyear': 'Winter',
  'newyears': 'Winter',
  
  // Fall holidays
  'halloween': 'Fall',
  'falloween': 'Fall',
  'black friday': 'Fall',
  'blackfriday': 'Fall',
  'thanksgiving': 'Fall',
  
  // Spring holidays
  'easter': 'Spring',
  'st patrick': 'Spring',
  "st patrick's": 'Spring',
  'st patricks': 'Spring',
  'stpatrick': 'Spring',
  
  // Summer holidays
  '4th of july': 'Summer',
  'fourth of july': 'Summer',
  'independence day': 'Summer',
  'memorial day': 'Summer',
  'labor day': 'Fall', // Actually early Fall
};

// ============================================
// PATTERNS
// ============================================

// Tag pattern (ALL_CAPS-Something or CAPS_Something)
// Examples: ANIMALS-Cats, MEDIA-CharlieBrown, HOLIDAY_Christmas
const TAG_PATTERN = /^[A-Z][A-Z0-9]*[-_][A-Za-z0-9]+$/;

// Year pattern (2015-2030)
const YEAR_PATTERN = /\b(20[1-3][0-9])\b/;

// Year with numeric month pattern: 2021/10, 2016/01, 2020-03
const YEAR_MONTH_PATTERN = /\b(20[1-3][0-9])[\/\-](\d{1,2})\b/;

// Year with text season pattern: 2014/Fall, 2015-Spring, 2020/Winter
const YEAR_SEASON_PATTERN = /\b(20[1-3][0-9])[\/\-](Spring|Summer|Fall|Autumn|Winter)\b/i;

// Year with text month pattern: 2014/March, 2015-Sept, 2020/Dec
const YEAR_TEXT_MONTH_PATTERN = /\b(20[1-3][0-9])[\/\-](Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\b/i;

// Standalone numeric month pattern (after year is removed): /10, /01
const STANDALONE_MONTH_PATTERN = /[\/\-](\d{1,2})\b/;

// ============================================
// EXCLUSIVE PATTERNS
// ============================================

// Patterns to detect exclusive releases and extract the retailer
const EXCLUSIVE_PATTERNS = [
  // "Exclusive to Color4Nails" → "Color4Nails"
  /^Exclusive\s+to\s+(.+)$/i,
  // "Exclusive - Color4Nails" → "Color4Nails"
  /^Exclusive\s*[-–—:]\s*(.+)$/i,
  // "Color4Nails Exclusive" → "Color4Nails"
  /^(.+?)\s+Exclusive$/i,
  // "Excl: Color4Nails" or "Excl. Color4Nails" → "Color4Nails"
  /^Excl[.:]?\s*(.+)$/i,
  // "C4N Exclusive" (common abbreviation) → "Color4Nails"
  /^C4N\s+Exclusive$/i,
];

// Common exclusive retailer abbreviations
const EXCLUSIVE_RETAILER_MAP: Record<string, string> = {
  'C4N': 'Color4Nails',
  'c4n': 'Color4Nails',
  'LynB': 'LynBDesigns',
  'FTLOP': 'For the Love of Polish',
};

// ============================================
// BOX/SUBSCRIPTION PATTERNS
// ============================================

// Patterns to detect subscription boxes and extract the box name
const BOX_PATTERNS = [
  // "For the Love of Polish Box" → "For the Love of Polish Box"
  /^(.+?\s+Box)$/i,
  // "Box: The Holo Hookup" → "The Holo Hookup"
  /^Box[:\s]+(.+)$/i,
  // "Subscription: Fandom Flakies" → "Fandom Flakies"
  /^Subscription[:\s]+(.+)$/i,
  // "Sub Box: Name" → "Name"
  /^Sub(?:scription)?\s*Box[:\s]+(.+)$/i,
];

// Known subscription box names (for detection even without "box" keyword)
const KNOWN_BOX_NAMES = [
  'For the Love of Polish',
  'FTLOP',
  'The Holo Hookup',
  'Holo Hookup',
  'Little Box of Horrors',
  'LBOH',
  'Fandom Flakies',
  'The Indie Spectrum',
  'Indie Spectrum',
  'Polish of the Month',
  'Anonamaker',
  'Anonamaker Box',
];

// ============================================
// GROUP CUSTOM PATTERNS
// ============================================

// Patterns to detect group customs
const GROUP_CUSTOM_PATTERNS = [
  // "Facebook Group: Holo Hangout" → "Holo Hangout"
  /^(?:Facebook|FB)\s*(?:Group)?[:\s]+(.+)$/i,
  // "Holo Hangout Custom" → "Holo Hangout"
  /^(.+?)\s+Custom$/i,
  // "GC: Holo Hangout" → "Holo Hangout"
  /^GC[:\s]+(.+)$/i,
  // "Group Custom: Name" → "Name"
  /^Group\s*Custom[:\s]+(.+)$/i,
  // "Reddit: lacqueristas" → "lacqueristas"  
  /^Reddit[:\s]+(.+)$/i,
];

// ============================================
// CONVENTION PATTERNS
// ============================================

// Patterns to detect conventions
const CONVENTION_PATTERNS = [
  // "Polish Con 2024" → "Polish Con"
  /^(Polish\s*Con)(?:\s+\d{4})?$/i,
  // "Indie Expo 2023" → "Indie Expo"
  /^(Indie\s*Expo)(?:\s+\d{4})?$/i,
  // "Convention: Polish Con" → "Polish Con"
  /^Convention[:\s]+(.+)$/i,
  // "Con: Name" → "Name"
  /^Con[:\s]+(.+)$/i,
];

// ============================================
// COLLAB PATTERNS
// ============================================

// Patterns to detect collaborations and extract both collaborator and theme
const COLLAB_PATTERNS = [
  // "Collab with Cupcake Polish: 4th Anniversary Duo" → collabWith: "Cupcake Polish", collabTheme: "4th Anniversary Duo"
  /^Collab\s+with\s+(.+?):\s*(.+)$/i,
  // "Collab with Cupcake Polish" (no theme) → collabWith: "Cupcake Polish"
  /^Collab\s+with\s+(.+)$/i,
  // "Cupcake Polish Collab: Anniversary Duo" → collabWith: "Cupcake Polish", collabTheme: "Anniversary Duo"
  /^(.+?)\s+Collab:\s*(.+)$/i,
  // "Cupcake Polish Collab" (no theme) → collabWith: "Cupcake Polish"
  /^(.+?)\s+Collab$/i,
];

// ============================================
// TYPES
// ============================================

export type ParseType = 'event' | 'collection' | 'both' | 'tag' | 'unknown';
export type Confidence = 'high' | 'medium' | 'low';

export interface ParsedCollection {
  parseType: ParseType;
  collectionName?: string;
  eventName?: string;         // Canonical event name field (renamed from eventSource)
  eventTheme?: string;        // For theme-based events like PPU, HHC
  releaseYear?: number;
  releaseSeason?: string;
  releaseMonth?: number;      // 1-12, more precise than season
  confidence: Confidence;
  // Additional structured fields
  boxName?: string;           // Subscription box name
  exclusiveTo?: string;       // Exclusive retailer
  groupName?: string;         // Group custom (Facebook group, etc.)
  conventionName?: string;    // Convention name
  collabWith?: string;        // Collaboration partner
  collabTheme?: string;       // Collaboration theme/occasion
  releaseType?: string;       // Detected release type
}

// ============================================
// HELPER: Check if word is followed by year
// ============================================

/**
 * Check if a word/phrase is followed by a year in the text.
 * Used to determine if "Summer" is a date ("Summer 2024") or part of a name ("Summer Breeze")
 */
function isFollowedByYear(text: string, word: string): boolean {
  // Create regex that looks for the word followed by optional space/punctuation and then a year
  const pattern = new RegExp(
    `\\b${word}\\b[\\s,;:\\-]*(?:20[1-3][0-9])\\b`,
    'i'
  );
  return pattern.test(text);
}

/**
 * Check if a year is preceded by a season/month/holiday word in the text.
 */
function isPrecededBySeasonWord(text: string, year: string): { word: string; season: string } | null {
  // Check seasons
  for (const season of SEASONS) {
    const pattern = new RegExp(`\\b${season}\\b[\\s,;:\\-]*${year}\\b`, 'i');
    if (pattern.test(text)) {
      return { word: season, season: season === 'Autumn' ? 'Fall' : season };
    }
  }
  
  // Check months
  for (const [monthName, season] of Object.entries(MONTHS_TO_SEASON)) {
    const pattern = new RegExp(`\\b${monthName}\\b[\\s,;:\\-]*${year}\\b`, 'i');
    if (pattern.test(text)) {
      return { word: monthName, season };
    }
  }
  
  // Check holidays (need to handle multi-word holidays)
  for (const [holiday, season] of Object.entries(HOLIDAYS_TO_SEASON)) {
    // Escape special regex characters and handle spaces
    const escapedHoliday = holiday.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
    const pattern = new RegExp(`\\b${escapedHoliday}\\b[\\s,;:\\-]*${year}\\b`, 'i');
    if (pattern.test(text)) {
      return { word: holiday, season };
    }
  }
  
  return null;
}

// ============================================
// MAIN PARSER FUNCTION
// ============================================

/**
 * Parse a collection/release field and extract structured data.
 * 
 * Examples:
 *   "Voltron Aug 2021"           → { collectionName: "Voltron", releaseSeason: "Summer", releaseYear: 2021 }
 *   "PPU March 2023 Rainbows"    → { eventSource: "Polish Pick Up", eventTheme: "Rainbows", releaseSeason: "Spring", releaseYear: 2023 }
 *   "Froggy Tales Sept 2024"     → { collectionName: "Froggy Tales", releaseSeason: "Fall", releaseYear: 2024 }
 *   "HHC Spooky Vibes Oct 2022"  → { eventSource: "Hella Handmade Creations", eventTheme: "Spooky Vibes", releaseSeason: "Fall", releaseYear: 2022 }
 *   "2021/10"                    → { releaseSeason: "Fall", releaseYear: 2021 }
 *   "2014/Fall"                  → { releaseSeason: "Fall", releaseYear: 2014 }
 *   "2015-Spring"                → { releaseSeason: "Spring", releaseYear: 2015 }
 *   "2014/March"                 → { releaseSeason: "Spring", releaseYear: 2014 }
 *   "Spring 2020; Beach Break"   → { collectionName: "Beach Break", releaseSeason: "Spring", releaseYear: 2020 }
 *   "Summer Breeze"              → { collectionName: "Summer Breeze" } (NO season - it's part of the name!)
 *   "Christmas 2024"             → { releaseSeason: "Winter", releaseYear: 2024 }
 *   "Halloween 2023"             → { releaseSeason: "Fall", releaseYear: 2023 }
 *   "Black Friday 2024"          → { releaseSeason: "Fall", releaseYear: 2024 }
 * 
 * @param value - The raw collection field value
 * @returns Parsed components with confidence level
 */
export function parseCollectionField(value: string): ParsedCollection {
  if (!value || !value.trim()) {
    return { parseType: 'unknown', confidence: 'low' };
  }

  const trimmed = value.trim();
  
  // Check if it's a tag pattern (ANIMALS-Cats, MEDIA-CharlieBrown)
  if (TAG_PATTERN.test(trimmed)) {
    return { 
      parseType: 'tag', 
      confidence: 'high' 
    };
  }

  let result: ParsedCollection = {
    parseType: 'unknown',
    confidence: 'low'
  };

  let remaining = trimmed;
  
  // ============================================
  // 0. CHECK FOR SPECIAL PATTERNS FIRST
  // ============================================
  
  // 0a. Check for EXCLUSIVE patterns
  for (const pattern of EXCLUSIVE_PATTERNS) {
    const match = remaining.match(pattern);
    if (match) {
      let retailer = match[1]?.trim() || '';
      // Check for abbreviations
      if (EXCLUSIVE_RETAILER_MAP[retailer]) {
        retailer = EXCLUSIVE_RETAILER_MAP[retailer];
      }
      if (retailer) {
        result.exclusiveTo = retailer;
        result.releaseType = 'exclusive';
        result.parseType = 'collection';
        result.confidence = 'high';
        // Remove the exclusive pattern, keep any remaining text (like year)
        remaining = remaining.replace(match[0], '').trim();
        break;
      }
    }
  }
  
  // 0b. Check for KNOWN BOX names (even without "box" keyword)
  if (!result.boxName) {
    const lowerTrimmed = trimmed.toLowerCase();
    for (const boxName of KNOWN_BOX_NAMES) {
      if (lowerTrimmed.includes(boxName.toLowerCase())) {
        // Extract just the box name, preserving original case where possible
        const boxRegex = new RegExp(boxName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
        const boxMatch = trimmed.match(boxRegex);
        if (boxMatch) {
          result.boxName = boxMatch[0];
          result.releaseType = 'box';
          result.parseType = 'collection';
          result.confidence = 'high';
          remaining = remaining.replace(boxMatch[0], '').trim();
          // Clean up any leftover "Box" word
          remaining = remaining.replace(/^\s*Box\s*/i, '').replace(/\s*Box\s*$/i, '').trim();
          break;
        }
      }
    }
  }
  
  // 0c. Check for BOX patterns (if not already matched)
  if (!result.boxName) {
    for (const pattern of BOX_PATTERNS) {
      const match = remaining.match(pattern);
      if (match) {
        const boxName = match[1]?.trim();
        if (boxName) {
          result.boxName = boxName;
          result.releaseType = 'box';
          result.parseType = 'collection';
          result.confidence = 'high';
          remaining = remaining.replace(match[0], '').trim();
          break;
        }
      }
    }
  }
  
  // 0d. Check for GROUP CUSTOM patterns
  for (const pattern of GROUP_CUSTOM_PATTERNS) {
    const match = remaining.match(pattern);
    if (match) {
      const groupName = match[1]?.trim();
      if (groupName) {
        result.groupName = groupName;
        result.releaseType = 'groupCustom';
        result.parseType = 'collection';
        result.confidence = 'high';
        remaining = remaining.replace(match[0], '').trim();
        break;
      }
    }
  }
  
  // 0e. Check for CONVENTION patterns
  for (const pattern of CONVENTION_PATTERNS) {
    const match = remaining.match(pattern);
    if (match) {
      const conventionName = match[1]?.trim();
      if (conventionName) {
        result.conventionName = conventionName;
        result.releaseType = 'convention';
        result.parseType = 'collection';
        result.confidence = 'high';
        remaining = remaining.replace(match[0], '').trim();
        break;
      }
    }
  }
  
  // 0f. Check for COLLAB patterns
  // These patterns extract both the collaborator name and optionally a theme
  for (const pattern of COLLAB_PATTERNS) {
    const match = remaining.match(pattern);
    if (match) {
      const collabWith = match[1]?.trim();
      const collabTheme = match[2]?.trim(); // May be undefined if pattern doesn't have theme group
      if (collabWith) {
        result.collabWith = collabWith;
        if (collabTheme) {
          result.collabTheme = collabTheme;
        }
        result.releaseType = 'collab';
        result.parseType = 'collection';
        result.confidence = 'high';
        remaining = remaining.replace(match[0], '').trim();
        break;
      }
    }
  }

  // ============================================
  // 1. YEAR/SEASON EXTRACTION (continue with remaining text)
  // ============================================

  // 1a. Check for year/season pattern first (handles "2014/Fall", "2015-Spring" format)
  const yearSeasonMatch = remaining.match(YEAR_SEASON_PATTERN);
  if (yearSeasonMatch) {
    result.releaseYear = parseInt(yearSeasonMatch[1]);
    const seasonText = yearSeasonMatch[2];
    // Normalize "Autumn" to "Fall"
    result.releaseSeason = seasonText.toLowerCase() === 'autumn' ? 'Fall' : 
      seasonText.charAt(0).toUpperCase() + seasonText.slice(1).toLowerCase();
    
    // Remove the entire year/season pattern from remaining text
    remaining = remaining.replace(yearSeasonMatch[0], '').trim();
  }
  // 1b. Check for year/text-month pattern (handles "2014/March", "2015-Sept" format)
  else {
    const yearTextMonthMatch = remaining.match(YEAR_TEXT_MONTH_PATTERN);
    if (yearTextMonthMatch) {
      result.releaseYear = parseInt(yearTextMonthMatch[1]);
      const monthText = yearTextMonthMatch[2].toLowerCase();
      
      // Find the season and month number for this month
      for (const [monthKey, season] of Object.entries(MONTHS_TO_SEASON)) {
        if (monthText.startsWith(monthKey) || monthKey.startsWith(monthText)) {
          result.releaseSeason = season;
          // Also set the month number
          if (MONTHS_TO_NUMBER[monthKey]) {
            result.releaseMonth = MONTHS_TO_NUMBER[monthKey];
          }
          break;
        }
      }
      
      // Remove the entire year/month pattern from remaining text
      remaining = remaining.replace(yearTextMonthMatch[0], '').trim();
    }
    // 1c. Extract year AND numeric month together (handles "2021/10" format)
    else {
      const yearMonthMatch = remaining.match(YEAR_MONTH_PATTERN);
      if (yearMonthMatch) {
        result.releaseYear = parseInt(yearMonthMatch[1]);
        const numericMonth = yearMonthMatch[2];
        
        // Convert numeric month to season
        if (NUMERIC_MONTHS_TO_SEASON[numericMonth]) {
          result.releaseSeason = NUMERIC_MONTHS_TO_SEASON[numericMonth];
        }
        // Also set the month number
        if (NUMERIC_MONTHS_TO_NUMBER[numericMonth]) {
          result.releaseMonth = NUMERIC_MONTHS_TO_NUMBER[numericMonth];
        }
        
        // Remove the entire year/month pattern from remaining text
        remaining = remaining.replace(yearMonthMatch[0], '').trim();
      } else {
        // 1d. Extract just year if no year/month combo
        const yearMatch = remaining.match(YEAR_PATTERN);
        if (yearMatch) {
          result.releaseYear = parseInt(yearMatch[1]);
          
          // Check if year is preceded by a season/month/holiday word
          const precededBy = isPrecededBySeasonWord(remaining, yearMatch[1]);
          if (precededBy) {
            result.releaseSeason = precededBy.season;
            // Remove both the word and the year
            const wordPattern = new RegExp(
              `\\b${precededBy.word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+')}\\b[\\s,;:\\-]*${yearMatch[1]}\\b`,
              'i'
            );
            remaining = remaining.replace(wordPattern, '').trim();
          } else {
            // Just remove the year
            remaining = remaining.replace(yearMatch[0], '').trim();
          }
        }
      }
    }
  }

  // 2. Check for leftover standalone numeric month (like "/10" or "/01")
  if (!result.releaseSeason) {
    const standaloneMonthMatch = remaining.match(STANDALONE_MONTH_PATTERN);
    if (standaloneMonthMatch) {
      const numericMonth = standaloneMonthMatch[1];
      if (NUMERIC_MONTHS_TO_SEASON[numericMonth]) {
        result.releaseSeason = NUMERIC_MONTHS_TO_SEASON[numericMonth];
      }
      if (NUMERIC_MONTHS_TO_NUMBER[numericMonth]) {
        result.releaseMonth = NUMERIC_MONTHS_TO_NUMBER[numericMonth];
      }
      remaining = remaining.replace(standaloneMonthMatch[0], '').trim();
    }
  }

  // 3. Check for explicit season words ONLY if followed by a year (already extracted above)
  //    or if no other text besides the season word
  //    "Summer 2024" → extract season (already handled above)
  //    "Summer" alone → extract season
  //    "Summer Breeze" → do NOT extract (it's the collection name)
  if (!result.releaseSeason) {
    for (const season of SEASONS) {
      const seasonRegex = new RegExp(`^\\s*${season}\\s*$`, 'i');
      if (seasonRegex.test(remaining)) {
        // It's ONLY the season word, extract it
        result.releaseSeason = season === 'Autumn' ? 'Fall' : season;
        remaining = '';
        break;
      }
    }
  }

  // 4. Check for month names ONLY if followed by a year (already extracted above)
  //    or if it's the only content
  //    "March 2023" → extract season (already handled above)
  //    "March" alone → extract season
  //    "March of the Penguins" → do NOT extract (it's the collection name)
  if (!result.releaseSeason) {
    for (const [monthName, season] of Object.entries(MONTHS_TO_SEASON)) {
      const monthRegex = new RegExp(`^\\s*${monthName}\\s*$`, 'i');
      if (monthRegex.test(remaining)) {
        result.releaseSeason = season;
        // Also set the month number
        if (MONTHS_TO_NUMBER[monthName]) {
          result.releaseMonth = MONTHS_TO_NUMBER[monthName];
        }
        remaining = '';
        break;
      }
    }
  }

  // 5. Check for holiday words ONLY if they were followed by a year (already handled above)
  //    "Christmas 2024" → already extracted above
  //    "Christmas Magic" → do NOT extract (it's the collection name)
  //    (No additional processing needed here - holidays are only extracted with years)

  // 6. Check for event keywords (sorted by length - longest first to avoid partial matches)
  let foundEventName: string | null = null;
  const sortedEventKeywords = Object.entries(EVENT_KEYWORD_MAP).sort((a, b) => b[0].length - a[0].length);
  
  for (const [keyword, fullName] of sortedEventKeywords) {
    const keywordRegex = new RegExp(`\\b${keyword.replace(/[&]/g, '\\&?').replace(/\s+/g, '\\s*')}\\b`, 'i');
    if (keywordRegex.test(remaining)) {
      foundEventName = fullName;
      remaining = remaining.replace(keywordRegex, '').trim();
      
      // Also remove all other variants that map to the same fullName
      for (const [variantKeyword, variantFullName] of sortedEventKeywords) {
        if (variantFullName === fullName && variantKeyword !== keyword) {
          const variantRegex = new RegExp(`\\b${variantKeyword.replace(/[&]/g, '\\&?').replace(/\s+/g, '\\s*')}\\b`, 'i');
          remaining = remaining.replace(variantRegex, '').trim();
        }
      }
      break;
    }
  }
  
  // Also check for full event names without abbreviation
  if (!foundEventName) {
    for (const [keyword, fullName] of sortedEventKeywords) {
      if (fullName.includes(' ') && fullName !== keyword) {
        const fullNameRegex = new RegExp(fullName.replace(/[&]/g, '\\&?').replace(/\s+/g, '\\s*'), 'i');
        if (fullNameRegex.test(trimmed)) {
          foundEventName = fullName;
          remaining = remaining.replace(fullNameRegex, '').trim();
          break;
        }
      }
    }
  }

  // 7. Clean up remaining text (including semicolons and other separators)
  remaining = remaining
    .replace(/^[-–—:,;\s\/]+/, '')   // Remove leading punctuation (added semicolon and slash)
    .replace(/[-–—:,;\s\/]+$/, '')   // Remove trailing punctuation (added semicolon and slash)
    .replace(/\s*[;:]\s*/g, ': ')    // Normalize semicolons/colons to ": "
    .replace(/\s*,\s*/g, ', ')       // Normalize comma spacing
    .replace(/\s+/g, ' ')            // Normalize spaces
    .trim();

  // 8. Determine parse type and set results
  if (foundEventName) {
    result.eventName = foundEventName;
    
    // Check if this is a theme-based event (PPU, HHC, etc.)
    const usesTheme = isThemeBasedEvent(foundEventName);
    
    if (remaining && remaining.length > 2) {
      if (usesTheme) {
        // Theme-based event: extra text is the THEME, not collection
        result.eventTheme = remaining;
        result.parseType = 'event'; // Still just "event" type
      } else {
        // Non-theme-based event: extra text is a collection within the event
        result.collectionName = remaining;
        result.parseType = 'both';
      }
    } else {
      result.parseType = 'event';
    }
    result.confidence = 'high';
  } else if (remaining) {
    // No event found, just a collection name
    result.parseType = 'collection';
    result.collectionName = remaining;
    result.confidence = (result.releaseYear || result.releaseSeason) ? 'high' : 'medium';
  }

  return result;
}

// ============================================
// HELPER: Apply parsed data to an item
// ============================================

/**
 * Given a raw collection value and existing item data, return the fields to update.
 * This is useful during import to spread parsed values into multiple fields.
 * 
 * @param rawValue - The raw collection field from CSV/Airtable
 * @param existingData - Any existing data (to avoid overwriting)
 * @returns Object with fields to set on the item
 */
export function applyParsedCollection(
  rawValue: string,
  existingData?: {
    collectionName?: string;
    eventName?: string;
    eventTheme?: string;
    releaseYear?: number;
    releaseSeason?: string;
    releaseMonth?: number;
    releaseType?: string;
  }
): {
  collectionName?: string;
  eventName?: string;
  eventTheme?: string;
  releaseYear?: number;
  releaseSeason?: string;
  releaseMonth?: number;
  releaseType?: string;
  parseType?: 'tag' | 'event' | 'collection' | 'both' | 'unknown';
} {
  const parsed = parseCollectionField(rawValue);
  const result: ReturnType<typeof applyParsedCollection> = {
    parseType: parsed.parseType,
  };

  // Skip if it's a tag pattern (needs manual review)
  if (parsed.parseType === 'tag') {
    // Just keep the original value as collection name
    result.collectionName = rawValue;
    return result;
  }

  // Set collection name (only if we parsed one and it's not a theme-based event)
  if (parsed.collectionName) {
    result.collectionName = parsed.collectionName;
  }

  // Set event name (only if we found one and it's not already set)
  if (parsed.eventName && !existingData?.eventName) {
    result.eventName = parsed.eventName;
  }

  // Set event theme (only if we found one and it's not already set)
  if (parsed.eventTheme && !existingData?.eventTheme) {
    result.eventTheme = parsed.eventTheme;
  }

  // Set release year (only if we found one and it's not already set)
  if (parsed.releaseYear && !existingData?.releaseYear) {
    result.releaseYear = parsed.releaseYear;
  }

  // Set release season (only if we found one and it's not already set)
  if (parsed.releaseSeason && !existingData?.releaseSeason) {
    result.releaseSeason = parsed.releaseSeason;
  }

  // Set release month (only if we found one and it's not already set)
  if (parsed.releaseMonth && !existingData?.releaseMonth) {
    result.releaseMonth = parsed.releaseMonth;
  }

  // Set release type based on what we found (only if not already set)
  if (!existingData?.releaseType || existingData.releaseType === 'unknown') {
    if (parsed.parseType === 'event') {
      result.releaseType = 'event';
    } else if (parsed.parseType === 'collection' || parsed.parseType === 'both') {
      result.releaseType = 'collection';
    }
  }

  return result;
}

// ============================================
// HELPER: Check if a value needs parsing
// ============================================

/**
 * Quick check to see if a collection value would benefit from parsing.
 * Useful to avoid unnecessary processing.
 * 
 * @param value - The collection field value
 * @returns true if the value contains parseable patterns
 */
export function needsParsing(value: string): boolean {
  if (!value || !value.trim()) return false;
  
  const trimmed = value.trim();
  
  // Check for EXCLUSIVE patterns
  for (const pattern of EXCLUSIVE_PATTERNS) {
    if (pattern.test(trimmed)) return true;
  }
  
  // Check for KNOWN BOX names
  const lowerTrimmed = trimmed.toLowerCase();
  for (const boxName of KNOWN_BOX_NAMES) {
    if (lowerTrimmed.includes(boxName.toLowerCase())) return true;
  }
  
  // Check for BOX patterns
  for (const pattern of BOX_PATTERNS) {
    if (pattern.test(trimmed)) return true;
  }
  
  // Check for GROUP CUSTOM patterns
  for (const pattern of GROUP_CUSTOM_PATTERNS) {
    if (pattern.test(trimmed)) return true;
  }
  
  // Check for CONVENTION patterns
  for (const pattern of CONVENTION_PATTERNS) {
    if (pattern.test(trimmed)) return true;
  }
  
  // Check for COLLAB patterns
  for (const pattern of COLLAB_PATTERNS) {
    if (pattern.test(trimmed)) return true;
  }
  
  // Check for year/season combo (2014/Fall, 2015-Spring)
  if (YEAR_SEASON_PATTERN.test(trimmed)) return true;
  
  // Check for year/text-month combo (2014/March, 2015-Sept)
  if (YEAR_TEXT_MONTH_PATTERN.test(trimmed)) return true;
  
  // Check for year/numeric-month combo (2021/10)
  if (YEAR_MONTH_PATTERN.test(trimmed)) return true;
  
  // Check for year
  if (YEAR_PATTERN.test(trimmed)) return true;
  
  // Check for season words followed by year
  for (const season of SEASONS) {
    if (isFollowedByYear(trimmed, season)) return true;
  }
  
  // Check for month names followed by year
  for (const monthName of Object.keys(MONTHS_TO_SEASON)) {
    if (isFollowedByYear(trimmed, monthName)) return true;
  }
  
  // Check for holiday words followed by year
  for (const holiday of Object.keys(HOLIDAYS_TO_SEASON)) {
    if (isFollowedByYear(trimmed, holiday)) return true;
  }
  
  // Check for event keywords
  for (const keyword of Object.keys(EVENT_KEYWORD_MAP)) {
    const regex = new RegExp(`\\b${keyword.replace(/[&]/g, '\\&?').replace(/\s+/g, '\\s*')}\\b`, 'i');
    if (regex.test(trimmed)) return true;
  }
  
  return false;
}

// ============================================
// HELPER: Format parsed result for display
// ============================================

/**
 * Format a parsed result for display in the UI (e.g., cleanup wizard preview)
 */
export function formatParsedResult(parsed: ParsedCollection): string {
  const parts: string[] = [];
  
  if (parsed.exclusiveTo) {
    parts.push(`⭐ Exclusive: ${parsed.exclusiveTo}`);
  }
  if (parsed.boxName) {
    parts.push(`📦 Box: ${parsed.boxName}`);
  }
  if (parsed.groupName) {
    parts.push(`👥 Group: ${parsed.groupName}`);
  }
  if (parsed.conventionName) {
    parts.push(`🎫 Convention: ${parsed.conventionName}`);
  }
  if (parsed.collabWith) {
    parts.push(`🤝 Collab: ${parsed.collabWith}`);
  }
  if (parsed.collabTheme) {
    parts.push(`Theme: ${parsed.collabTheme}`);
  }
  if (parsed.eventName) {
    parts.push(`🎪 Event: ${parsed.eventName}`);
  }
  if (parsed.eventTheme) {
    parts.push(`Theme: ${parsed.eventTheme}`);
  }
  if (parsed.collectionName) {
    parts.push(`📚 Collection: ${parsed.collectionName}`);
  }
  if (parsed.releaseSeason) {
    const emoji = {
      'Spring': '🌷',
      'Summer': '☀️',
      'Fall': '🍂',
      'Winter': '❄️'
    }[parsed.releaseSeason] || '';
    parts.push(`${emoji} ${parsed.releaseSeason}`);
  }
  if (parsed.releaseYear) {
    parts.push(`${parsed.releaseYear}`);
  }
  
  return parts.join(' • ');
}
